import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  ListItemButtonProps,
  ListItemProps,
  ListItemSecondaryAction,
} from "@mui/material";
import { CasLoader } from "../brand/CasLoader";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppIdMap } from "../../hook/app";
import { useMenu } from "../../hook/menu";
import {
  useIsNoteboookPageSelected,
  useNotebookPage,
  useNotebookPagePath,
  useSelectedNotebookPageIdMap,
} from "../../hook/page";
import { selectRootNotebookPages } from "../../selectors/page";
import { EditPageMenu } from "../menu/page";
import {
  useIsAncestorOfSelected,
  useFetchParentSubPagesListener,
  useFetchSubPages,
  useSideNavMenu,
  useSubPages,
} from "../../hook/sideNav";
import { IconButtonSmall, ListSmall } from "./listItem";
import { SideNavMenuProvider } from "./provider";
import { AddPageModal, SideNavMenu } from "./sideNavMenu";

export const NotebookPageSubPagesListenerComponent: React.FC<{
  pageId: string;
}> = React.memo(({ pageId }) => {
  const { isOpen } = useSideNavMenu();
  const subPages = useSubPages(pageId);
  const hasSubPages = subPages !== undefined;
  const fetchSubPages = useFetchSubPages();

  useEffect(() => {
    if (hasSubPages) return;
    if (!isOpen) return;

    fetchSubPages(pageId);
  }, [pageId, isOpen, hasSubPages]);

  return <></>;
});

export const NotebookExplorerSubPagesList: React.FC<{
  pageId: string;
  indentLevel: number;
}> = React.memo(({ pageId, indentLevel }) => {
  const subPages = useSubPages(pageId);

  return (
    <ListSmall>
      {subPages?.map((subPage) => (
        <NotebookExplorerPage
          key={subPage["page-id"]}
          pageId={subPage["page-id"]}
          indentLevel={indentLevel + 1}
        />
      ))}
    </ListSmall>
  );
});

export const NotebookExplorerPage: React.FC<{
  pageId: string;
  indentLevel: number;
}> = React.memo(({ pageId, indentLevel }) => {
  const { page } = useNotebookPage(pageId);
  const subPages = useSubPages(pageId);
  const isLoading = subPages === undefined;
  const notebookPagePath = useNotebookPagePath();
  const isSelected = useIsNoteboookPageSelected(pageId);
  const isAncestor = useIsAncestorOfSelected(pageId);
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const reopenKey = isAncestor || isSelected ? selectedNotebookPageId : undefined;
  const buttonProps: ListItemButtonProps = useMemo(
    () => (isSelected ? { selected: true } : {}),
    [isSelected],
  );
  const { anchorEl, handleOpen, onClose, open } = useMenu();
  const listItemProps: ListItemProps = useMemo(
    () => ({ sx: { pl: indentLevel }, onContextMenu: handleOpen }),
    [indentLevel, handleOpen],
  );

  return (
    <SideNavMenuProvider
      initIsOpen={false}
      controlledIsOpen={isAncestor || isSelected}
      reopenKey={reopenKey}
    >
      <EditPageMenu anchorEl={anchorEl} onClose={onClose} open={open} pageId={pageId} />
      <SideNavMenu
        name={page?.["page-data"].name ?? "Loading..."}
        props={listItemProps}
        href={notebookPagePath({ pageId })}
        buttonProps={buttonProps}
        testId={`side-nav-page-${pageId}`}
      >
        <NotebookPageSubPagesListenerComponent pageId={pageId} />
        {isLoading ? (
          <Box sx={{ pl: indentLevel + 1 }}>
            <CasLoader size={20} />
          </Box>
        ) : (
          <NotebookExplorerSubPagesList
            pageId={pageId}
            indentLevel={indentLevel}
          />
        )}
      </SideNavMenu>
    </SideNavMenuProvider>
  );
});

export const NotebookExplorer: React.FC = React.memo(() => {
  useFetchParentSubPagesListener();
  const pages = useSelector(selectRootNotebookPages);
  const { appId } = useAppIdMap();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <AddPageModal open={modalOpen} setOpen={setModalOpen} />
      <SideNavMenuProvider initIsOpen={true}>
        <SideNavMenu
          name="Notebook"
          href={`/app/${appId}/notebook`}
          testId="side-nav-notebook"
          buttons={
            <>
              <ListItemSecondaryAction>
                <IconButtonSmall
                  onClick={() => {
                    setModalOpen(true);
                  }}
                  color="primary"
                  edge="end"
                  aria-label="add"
                >
                  <AddIcon />
                </IconButtonSmall>
              </ListItemSecondaryAction>
            </>
          }
        >
          <ListSmall>
            {pages.map((page) => (
              <NotebookExplorerPage
                key={page["page-id"]}
                pageId={page["page-id"]}
                indentLevel={2}
              />
            ))}
          </ListSmall>
        </SideNavMenu>
      </SideNavMenuProvider>
    </>
  );
});
